import React from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { SectionProps } from '../../types';
import { IconPattern } from './IconPattern';

/**
 * Section component for creating consistent page sections
 * Supports different backgrounds, padding, and animations
 */

const SectionBase = styled(motion.section)<SectionProps>`
  position: relative;
  overflow: hidden;

  ${props => props.padding === 'small' && css`
    padding: ${props.theme.spacing[16]} 0;
  `}

  ${props => props.padding === 'medium' && css`
    padding: ${props.theme.spacing[24]} 0;
  `}

  ${props => props.padding === 'large' && css`
    padding: ${props.theme.spacing[32]} 0;
  `}

  ${props => props.background === 'light' && css`
    background-color: ${props.theme.colors.background};
  `}

  ${props => props.background === 'dark' && css`
    background-color: ${props.theme.colors.surface};
  `}

  ${props => props.background === 'gradient' && css`
    background: ${props.theme.gradients.cosmic};
    color: white;

    h1, h2, h3, h4, h5, h6 {
      color: white;
    }

    p {
      color: rgba(255, 255, 255, 0.9);
    }
  `}

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    ${props => props.padding === 'small' && css`
      padding: ${props.theme.spacing[12]} 0;
    `}

    ${props => props.padding === 'medium' && css`
      padding: ${props.theme.spacing[16]} 0;
    `}

    ${props => props.padding === 'large' && css`
      padding: ${props.theme.spacing[20]} 0;
    `}
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing[4]};

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: 0 ${props => props.theme.spacing[4]};
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    padding: 0 ${props => props.theme.spacing[3]};
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing[16]};

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    margin-bottom: ${props => props.theme.spacing[12]};
  }
`;

const SectionTitle = styled(motion.h2)`
  font-family: ${props => props.theme.typography.fonts.heading};
  font-size: ${props => props.theme.typography.sizes['4xl']};
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing[4]};
  line-height: ${props => props.theme.typography.lineHeights.tight};
  position: relative;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.sizes['3xl']};
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.typography.sizes['2xl']};
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -${props => props.theme.spacing[2]};
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: ${props => props.theme.gradients.primary};
    border-radius: ${props => props.theme.borderRadius.full};
  }
`;

const SectionSubtitle = styled(motion.p)`
  font-size: ${props => props.theme.typography.sizes.lg};
  color: ${props => props.theme.colors.textSecondary};
  max-width: 600px;
  margin: 0 auto;
  line-height: ${props => props.theme.typography.lineHeights.relaxed};

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.sizes.base};
  }
`;

const SectionContent = styled.div`
  position: relative;
  z-index: 1;
`;

// Suppression du BackgroundPattern - remplacé par IconPattern

const Section: React.FC<SectionProps> = ({
  id,
  title,
  subtitle,
  background = 'light',
  padding = 'medium',
  children,
  className,
  as,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
}) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const subtitleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: 0.1 },
    },
  };

  return (
    <SectionBase
      as={as}
      id={id}
      background={background}
      padding={padding}
      className={className}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-100px' }}
    >
      <IconPattern sectionId={id || ''} />
      <Container>
        <SectionContent>
          {(title || subtitle) && (
            <SectionHeader>
              {title && (
                <SectionTitle variants={titleVariants}>
                  {title}
                </SectionTitle>
              )}
              {subtitle && (
                <SectionSubtitle variants={subtitleVariants}>
                  {subtitle}
                </SectionSubtitle>
              )}
            </SectionHeader>
          )}
          {children}
        </SectionContent>
      </Container>
    </SectionBase>
  );
};

export default Section;
